import React, { useContext, useEffect, useState } from "react";
import { TitleCard } from "../../shared-components/Cards";
import { Subscription } from "./Subscription";
import { Grid, Typography, TextField, Stack } from "@mui/material";
import { WorkspaceContext } from "../../utils/context";
import { useAccountMutation, useIsOrgAdmin } from "../../crud/account/hooks";
import { useForm } from "react-hook-form";
import { LoadingButton } from "@mui/lab";
import { OrganizationSelector } from "../../shared-components/OrganizationSelector";
import { OrganizationScopeSchema } from "../../api-client";
import { useOrganization } from "../../crud/organizations/hooks";
import { SelectSomething } from "../../shared-components/SelectSomething";
import { UserContext } from "../../crud/user/context";

export const CreditLimitSettings = (): React.ReactElement => {
  const mutateAccount = useAccountMutation();
  const viewedAccount = useContext(WorkspaceContext);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<{ creditLimit: number }>({});

  useEffect(() => {
    reset({ creditLimit: viewedAccount?.creditLimit });
  }, [viewedAccount?.creditLimit, reset]);

  const onSubmit = (data: { creditLimit: number }) => {
    mutateAccount.mutate(data.creditLimit);
  };
  return (
    <TitleCard title="Monthly Workspace Credit limit">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2} alignItems={"flex-start"}>
          <Typography>
            Set a limit on the total number of credits (CPU hours) this
            workspace can use per month (including both free and paid credits).
          </Typography>
          <TextField
            label="Limit"
            type="number"
            error={!!errors.creditLimit}
            {...register("creditLimit", {
              validate: (value) => {
                if (value < 0) {
                  return "Credit limit must be positive";
                }
                if (value > 2147483647) {
                  return "Credit limit must be less than 2147483647";
                }
              },
              required: "Credit limit is required",
            })}
            helperText={errors.creditLimit?.message}
          />
          <LoadingButton
            disabled={!isDirty}
            loading={mutateAccount.isLoading}
            type="submit"
          >
            Save
          </LoadingButton>
        </Stack>
      </form>
    </TitleCard>
  );
};

export const Billing = (): React.ReactElement => {
  const { user: isStaff } = useContext(UserContext);

  const [org, setOrg] = useState<OrganizationScopeSchema | undefined>(
    undefined,
  );
  const { data: organization, isSuccess: organizationLoaded } = useOrganization(
    org?.id,
  );
  const isOrgAdmin = useIsOrgAdmin(org?.id);
  return (
    <Stack spacing={2}>
      <div style={{ marginLeft: "auto" }}>
        <OrganizationSelector onChange={setOrg} />
      </div>
      {(isOrgAdmin || isStaff) &&
        organizationLoaded &&
        organization.activeProgramId && (
          <Grid item xs={6} md={6}>
            <TitleCard title="Subscription">
              <Subscription
                programId={organization.activeProgramId}
                organizationId={organization.id}
              />
            </TitleCard>
          </Grid>
        )}
      {!org && (
        <SelectSomething message="Select an organization to view billing information." />
      )}
      {org && !isOrgAdmin && !isStaff && (
        <SelectSomething
          message={`You must be an admin to view billing information for ${org.name}.`}
        />
      )}
    </Stack>
  );
};
